import React, { useState } from 'react';

import { Search } from '@videoblocks/react-icons';

import TopNavSearchBar from './TopNavSearchBar';
import DesktopNavActions from './desktop/DesktopNavActions';
import DesktopNavItem from './desktop/DesktopNavItem';
import MobileNav from './mobile/MobileNav';
import TabletNavActions from './tablet/TabletNavActions';
import type {
  DesktopNavConfig,
  MobileNavConfig,
  TabletNavConfig,
} from './types';

interface TopNavigationBarProps {
  desktop: DesktopNavConfig;
  tablet: TabletNavConfig;
  mobile: MobileNavConfig;
  logoUrl: string;
  showSearch?: boolean;
}

export default function TopNavigationBar({
  desktop,
  tablet,
  mobile,
  logoUrl,
  showSearch = false,
}: TopNavigationBarProps): JSX.Element {
  const [isSearchExpanded, setIsSearchExpanded] = useState(showSearch);

  return (
    <nav
      aria-label="Main navigation"
      className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white"
    >
      <div className="flex items-center justify-between p-4 topnav-xl:px-6 topnav-xl:py-4">
        <div className="flex items-center gap-5">
          <a href="/" className="flex-shrink-0">
            <img src={logoUrl} alt="Storyblocks" width={151} height={32} />
          </a>
          {/* Tablet inline items (lg to xl) */}
          <div className="hidden items-center lg:flex topnav-xl:hidden">
            {tablet.navItems.map((item) => (
              <DesktopNavItem key={item.id} config={item} />
            ))}
          </div>
          {/* Desktop inline items (xl+) */}
          <div className="hidden items-center topnav-xl:flex">
            {desktop.navItems.map((item) => (
              <DesktopNavItem key={item.id} config={item} />
            ))}
            {!isSearchExpanded && (
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                aria-label="Open search"
                className="ml-2 p-2 text-gray-600 hover:text-gray-900"
              >
                <Search className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <DesktopNavActions items={desktop.actions} />
          <TabletNavActions config={tablet} logoUrl={logoUrl} />
          <MobileNav config={mobile} logoUrl={logoUrl} />
        </div>
      </div>

      {/* Below xl: always rendered, hidden at xl via CSS — no JS, no flash */}
      <div className="flex justify-center pb-2 px-4 topnav-xl:hidden">
        <TopNavSearchBar onCollapse={() => {}} showXButton={false} />
      </div>

      {/* xl and up: render only when expanded; SSR matches because
          isSearchExpanded is initialized from the server-passed showSearch */}
      {isSearchExpanded && (
        <div className="hidden justify-center pb-2 px-6 topnav-xl:flex">
          <TopNavSearchBar
            onCollapse={() => setIsSearchExpanded(false)}
            showXButton
          />
        </div>
      )}
    </nav>
  );
}
